import { Component, OnDestroy, OnInit } from '@angular/core';
import { EcondosQuery } from '@api/model/query';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Subject } from 'rxjs';
import { takeUntil, timeout } from 'rxjs/operators';
import { CondoTypeLabel, NotificationChannelLabelMap, SystemBroadcast, UserTypeLabel } from '@api/model/system-broadcast';
import { SystemBroadcastService } from '@api/service/system-broadcast.service';

type Status = 'LOADING' | 'SUCCESS' | 'ERROR';

type SystemBroadcastToggleDTO = Pick<SystemBroadcast, '_id' | 'isActive'>;

@Component({
  selector: 'app-system-broadcasts-page',
  templateUrl: './system-broadcast-page.component.html',
  styleUrls: ['./system-broadcast-page.component.scss']
})
export class SystemBroadcastPageComponent implements OnInit, OnDestroy {
  systemBroadcasts: SystemBroadcast[] = [];
  systemBroadcastsData: {
    _id: string;
    title: string;
    createdAt: string;
    isActive: boolean;
    whiteLabels?: string;
    notificationChannel?: string;
    userTypes: string;
    condoTypes: string;
    linkButtonClickCount?: number;
    startDate: string;
    endDate?: string;
  }[] = [];
  nzPageSize = 10;
  nzPageIndex = 0;
  totalData = 0;
  status: Status = 'LOADING';

  initialQuery: EcondosQuery = {
    $select: 'title createdAt isActive whiteLabel userTypes condoTypes notificationChannel linkButtonClickCount condos startDate endDate',
    $populate: [
      { path: 'whiteLabels', select: 'companyName' },
      { path: 'condos', select: 'name' }
    ],
    $sort: '-createdAt'
  };

  notificationChannelLabelMap = NotificationChannelLabelMap;

  unsubscribe$: Subject<boolean> = new Subject();

  constructor(
    private systemBroadcastService: SystemBroadcastService,
    private notificationService: NzNotificationService
  ) {}

  ngOnInit(): void {
    this.getData();
  }

  ngOnDestroy(): void {
    if (this.unsubscribe$) {
      this.unsubscribe$.next(true);
      this.unsubscribe$.complete();
    }
  }

  getData() {
    const query = this.initialQuery;

    query.$page = this.nzPageIndex;
    query.$limit = this.nzPageSize;

    this.status = 'LOADING';

    this.systemBroadcastService
      .get(query)
      .pipe(timeout(20_000), takeUntil(this.unsubscribe$))
      .subscribe({
        next: response => {
          this.totalData = response.count;
          this.systemBroadcasts = response.systemBroadcasts;
          this.parseDataToTable(response.systemBroadcasts);
          this.status = 'SUCCESS';
        },
        error: err => {
          const errorMessage = err?.originalError?.message || 'Ocorreu um erro ao tentar buscar os avisos. Tente novamente, por favor.';
          this.notificationService.error('Erro', errorMessage);
          this.status = 'ERROR';
        }
      });
  }

  handleDelete(dataToDelete: SystemBroadcast) {
    const notification = this.notificationService.info('Excluindo', 'Excluindo este aviso...', { nzDuration: 0 });

    this.systemBroadcastService
      .delete(dataToDelete._id)
      .pipe(timeout(10000))
      .subscribe({
        next: () => {
          this.notificationService.remove(notification.messageId);
          this.notificationService.success('Sucesso', 'Aviso excluído com sucesso.');
          this.getData();
        },
        error: err => {
          const errorMessage = err?.originalError?.message || 'Ocorreu um erro ao tentar excluir o aviso. Tente novamente, por favor.';
          this.notificationService.remove(notification.messageId);
          this.notificationService.error('Erro', errorMessage);
        }
      });
  }

  handleToggle(data: SystemBroadcastToggleDTO) {
    const isActive = !data.isActive;

    const notification = this.notificationService.info(
      !isActive ? 'Desativando' : 'Ativando',
      `${!isActive ? 'Desativando' : 'Ativando'} este destaque...`,
      { nzDuration: 0 }
    );

    this.systemBroadcastService
      .toggle(data._id)
      .pipe(timeout(20000))
      .subscribe({
        next: () => {
          this.systemBroadcastsData = this.systemBroadcastsData.map(systemBroadcast =>
            data._id === systemBroadcast._id ? { ...systemBroadcast, isActive } : systemBroadcast
          );

          this.notificationService.remove(notification.messageId);
          this.notificationService.success('Sucesso', `Aviso ${isActive ? 'ativado' : 'desativado'} com sucesso.`);
        },
        error: err => {
          const errorMessage =
            err?.originalError?.message ||
            `Ocorreu um erro ao tentar ${isActive ? 'ativar' : 'desativar'} o destaque. Tente novamente, por favor.`;

          this.notificationService.remove(notification.messageId);
          this.notificationService.error('Erro', errorMessage);
        }
      });
  }

  refreshIndexChange(value: any) {
    this.nzPageIndex = value - 1;
    this.getData();
  }

  refreshSizeChange(value: any) {
    this.nzPageSize = value;
    this.getData();
  }

  parseDataToTable(systemBroadcasts: SystemBroadcast[]) {
    this.systemBroadcastsData = systemBroadcasts.map(data => {
      let whiteLabelCount = 0;
      const whiteLabels =
        data?.whiteLabels &&
        data.whiteLabels?.reduce((acc, cur) => {
          if (whiteLabelCount <= 4) {
            if (acc) {
              acc = acc.concat(', ');
            }
            acc = acc.concat(cur.companyName);
            whiteLabelCount++;
          } else if (whiteLabelCount === 5) {
            acc = acc.concat('...');
            whiteLabelCount++;
          }
          return acc;
        }, '');
      const userTypes = data?.userTypes?.reduce((acc, cur) => {
        if (acc) {
          acc = acc.concat(', ');
        }
        acc = acc.concat(UserTypeLabel[cur]);
        return acc;
      }, '');
      const condoTypes = data?.condoTypes?.reduce((acc, cur) => {
        if (acc) {
          acc = acc.concat(', ');
        }
        acc = acc.concat(CondoTypeLabel[cur]);
        return acc;
      }, '');
      return {
        _id: data._id,
        title: data.title,
        createdAt: data.createdAt,
        isActive: data.isActive,
        notificationChannel: data.notificationChannel,
        whiteLabels,
        userTypes,
        condoTypes,
        linkButtonClickCount: data?.linkButtonClickCount || 0,
        startDate: data.startDate,
        endDate: data.endDate || ''
      };
    });
  }
}
